/**
 * Defensive guard for AGENT message content.
 *
 * The thread agent is supposed to store only its `message` field as the entry
 * content. A malformed or legacy agent response can instead leak the entire
 * `AgentOutput` JSON envelope — e.g.
 * `{ "actions": [], "message": "…", "message_reason": "clarification", "references": [] }`
 * — into the stored content, which then renders as raw JSON in the chat bubble.
 *
 * `stripAgentEnvelope` scans for the first balanced `{…}` object that has the
 * shape of an envelope (a string `message` plus an envelope-distinctive key)
 * and returns its inner `message`. It mirrors the backend parser, so prose with
 * surrounding/trailing braces — or a stray brace before the real JSON — is
 * handled the same way. For normal markdown/text it returns the content
 * unchanged, and it never throws.
 *
 * Apply this only to AGENT_RESPONSE content — user/external contributions are
 * raw and must never be rewritten.
 */

/** Yield each balanced `{…}` object in `text`, ignoring braces inside strings. */
function* balancedObjects(text: string): Generator<string> {
    let i = 0;
    const n = text.length;
    while (i < n) {
        if (text[i] !== "{") {
            i++;
            continue;
        }
        let depth = 0;
        let inString = false;
        let escaped = false;
        let end = -1;
        for (let j = i; j < n; j++) {
            const ch = text[j];
            if (inString) {
                if (escaped) escaped = false;
                else if (ch === "\\") escaped = true;
                else if (ch === '"') inString = false;
                continue;
            }
            if (ch === '"') inString = true;
            else if (ch === "{") depth++;
            else if (ch === "}") {
                depth--;
                if (depth === 0) {
                    end = j;
                    break;
                }
            }
        }
        if (end !== -1) {
            yield text.slice(i, end + 1);
            i = end + 1;
        } else {
            // Unbalanced from here — advance one char and keep scanning.
            i++;
        }
    }
}

/** True if `data` has the distinctive shape of an AgentOutput envelope. */
const isAgentEnvelope = (data: unknown): data is { message: string } =>
    data !== null &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    typeof (data as Record<string, unknown>).message === "string" &&
    ("actions" in data || "message_reason" in data || "references" in data);

export const stripAgentEnvelope = (content: string): string => {
    if (!content) return content;
    for (const candidate of balancedObjects(content)) {
        let parsed: unknown;
        try {
            parsed = JSON.parse(candidate);
        } catch {
            continue;
        }
        if (isAgentEnvelope(parsed)) return parsed.message;
    }
    return content;
};

/** Posted by the Confirm/Cancel buttons; shared with the renderer so they can't drift. */
export const PROPOSAL_CONFIRM_MESSAGE = "Confirmed — go ahead.";
export const PROPOSAL_CANCEL_MESSAGE = "No — cancel that.";

/**
 * Human-readable size for an attachment chip. The byte count rides on the FILE
 * context item as a string; a missing/zero/garbage value renders as "" (no size).
 */
export const humanSize = (bytes: string | undefined): string => {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n <= 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export type ThreadContentSegment =
    { type: "markdown"; text: string } | { type: "html"; source: string };

/**
 * Split chat markdown into prose and fenced ```html``` snippets so the
 * timeline can render those snippets as live previews (same as rich-text docs).
 * An unclosed fence stays markdown so the user still sees the source.
 */
const HTML_FENCE = /```html[^\n`]*\n([\s\S]*?)```/gi;

export const splitThreadContent = (content: string): ThreadContentSegment[] => {
    if (!content) return [];
    const segments: ThreadContentSegment[] = [];
    let lastIndex = 0;
    HTML_FENCE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = HTML_FENCE.exec(content)) !== null) {
        if (match.index > lastIndex) {
            segments.push({
                type: "markdown",
                text: content.slice(lastIndex, match.index),
            });
        }
        segments.push({ type: "html", source: match[1] });
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex === 0) {
        return [{ type: "markdown", text: content }];
    }
    if (lastIndex < content.length) {
        segments.push({ type: "markdown", text: content.slice(lastIndex) });
    }
    return segments;
};
